import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
  const from = safeNext || location.state?.from || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const r = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success(`Welcome back, ${r.user.name}`);
    navigate(r.user.role === "admin" && !safeNext ? "/admin" : from, { replace: true });
  };

  return (
    <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl grid-cols-1 items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-2">
      <div className="hidden lg:block">
        <div className="brand-gradient relative overflow-hidden rounded-3xl p-10 text-white card-elevated">
          <div className="font-display text-xs font-bold uppercase tracking-[0.25em] text-white/70">Welcome back</div>
          <h2 className="mt-2 font-display text-3xl font-bold leading-tight">
            Pick up where you left off<br />in the flight deck.
          </h2>
          <p className="mt-4 max-w-md text-sm text-white/80">
            Continue practising, finish that mock, and climb those accuracy curves. Every question gets you closer to the cockpit.
          </p>
          <div className="absolute -right-6 -bottom-6 h-56 w-56 rounded-full bg-[hsl(var(--brand-orange))]/25 blur-3xl" />
        </div>
      </div>

      <Card className="mx-auto w-full max-w-md border-border/70 card-elevated">
        <CardContent className="p-8">
          <div className="mb-6"><BrandLogo /></div>
          <h1 className="font-display text-2xl font-bold">Log in to PilotYatra</h1>
          <p className="mt-1 text-sm text-muted-foreground">Access your dashboard, bookmarks and quiz history.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground"
                  tabIndex={-1}
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm text-muted-foreground">
            New here?{" "}
            <Link to={`/register${location.search}`} className="font-semibold text-[hsl(var(--brand-navy))] hover:underline" data-testid="go-to-register">
              Create an account
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
